import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle2, XCircle } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  CreatorAdsInfo,
  MarketplaceAd,
  getAdMarketplace,
  getCreatorAds,
  setAdOptIn,
  setCreatorAdsEnabled,
  setCreatorAdsMode,
} from '../../../api/ads/ads.api';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const Kpi: React.FC<{ label: string; value: string; accent?: boolean }> = ({ label, value, accent }) => (
  <div className="rounded-2xl ui-card border ui-divider p-5">
    <div className="text-sm ui-muted mb-1">{label}</div>
    <div className={`text-2xl font-extrabold ${accent ? 'text-[#0071A6]' : 'ui-text'}`}>{value}</div>
  </div>
);

const num = (n?: number) => (n ?? 0).toLocaleString('vi-VN');

const Marketplace: React.FC<{ manual: boolean }> = ({ manual }) => {
  const [items, setItems] = useState<MarketplaceAd[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<Set<string>>(new Set()); // tick đang gửi: khoá công tắc

  const load = (p: number) =>
    getAdMarketplace(p)
      .then((r) => {
        setItems((prev) => (p === 1 ? r.items : [...prev, ...r.items]));
        setTotal(r.total);
        setPage(p);
      })
      .catch((e: any) => toast.error(e?.response?.data?.message || 'Không tải được sàn quảng cáo.'))
      .finally(() => setLoaded(true));

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (it: MarketplaceAd) => {
    if (busy.has(it.campaignId)) return;
    const next = !it.optedIn;
    setBusy((b) => new Set(b).add(it.campaignId));
    try {
      await setAdOptIn(it.campaignId, next);
      setItems((prev) => prev.map((x) => (x.campaignId === it.campaignId ? { ...x, optedIn: next } : x)));
    } catch (e: any) {
      // Lỗi: công tắc giữ nguyên trạng thái cũ (chỉ đổi khi server xác nhận).
      toast.error(e?.response?.data?.message || 'Không lưu được, thử lại sau.');
    } finally {
      setBusy((b) => {
        const n = new Set(b);
        n.delete(it.campaignId);
        return n;
      });
    }
  };

  const anyTicked = items.some((i) => i.optedIn);

  return (
    <div className="bg-white dark:bg-dark2 shadow-lg dark:shadow-none p-4 rounded-md space-y-3">
      <span className="font-bold text-lg">Sàn quảng cáo</span>
      {manual && loaded && items.length >= total && !anyTicked && items.length > 0 && (
        <p className="text-sm text-amber-600">
          Bạn chưa chọn doanh nghiệp nào nên nội dung của bạn chưa hiện quảng cáo.
        </p>
      )}
      {loaded && items.length === 0 ? (
        <p className="text-sm ui-muted">Chưa có doanh nghiệp nào đăng quảng cáo lên sàn.</p>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.campaignId} className="rounded-xl ui-divider border p-3 flex items-start gap-3">
              {it.imageUrl ? (
                <img src={it.imageUrl} alt="" className="w-16 h-16 rounded-md object-cover shrink-0" />
              ) : (
                <div className="w-16 h-16 rounded-md bg-slate-100 dark:bg-slate-700 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs ui-muted">{it.advertiserName}</div>
                <div className="font-medium ui-text">{it.title}</div>
                {it.body ? <div className="text-sm ui-muted line-clamp-2">{it.body}</div> : null}
                <div className="text-xs ui-muted mt-1">
                  {it.categories?.length ? it.categories.join(', ') : null}
                  {it.landingDomain ? ` · ${it.landingDomain}` : ''}
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs ui-text shrink-0 cursor-pointer">
                <input
                  type="checkbox"
                  role="switch"
                  aria-label={`Hiện trên nội dung của tôi: ${it.title || it.advertiserName}`}
                  checked={it.optedIn}
                  disabled={busy.has(it.campaignId)}
                  onChange={() => toggle(it)}
                />
                Hiện trên nội dung của tôi
              </label>
            </div>
          ))}
        </div>
      )}
      {items.length < total && (
        <button
          type="button"
          onClick={() => load(page + 1)}
          className="rounded-md ui-divider border px-3 py-1.5 text-sm ui-text"
        >
          Xem thêm
        </button>
      )}
    </div>
  );
};

const CreatorAdsSetting: React.FC = () => {
  const [info, setInfo] = useState<CreatorAdsInfo | null>(null);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    getCreatorAds()
      .then((r) => alive && setInfo(r))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, []);

  const toggle = async () => {
    if (!info || saving) return;
    setSaving(true);
    try {
      setInfo(await setCreatorAdsEnabled(!info.adsEnabled));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Không lưu được, thử lại sau.');
    } finally {
      setSaving(false);
    }
  };

  const changeMode = async (mode: 'Auto' | 'Manual') => {
    if (!info || saving || info.mode === mode) return;
    setSaving(true);
    try {
      setInfo(await setCreatorAdsMode(mode));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Không lưu được, thử lại sau.');
    } finally {
      setSaving(false);
    }
  };

  if (failed) return <p className="ui-muted text-sm">Không tải được dữ liệu, thử lại sau.</p>;
  if (!info) return <p className="ui-muted text-sm">Đang tải…</p>;

  const checks = [
    { ok: info.checks.isCreator, label: 'Tài khoản Content Creator', to: '/settings?tab=xac-minh', action: 'Làm ngay' },
    { ok: info.checks.ekyc, label: 'Đã xác minh danh tính (eKYC)', to: '/settings?tab=xac-minh', action: 'Làm ngay' },
    { ok: info.checks.donate, label: 'Đã mở nhận donate', to: '/settings?tab=donate', action: 'Gửi yêu cầu mở donate' },
  ];

  const chartData = {
    labels: info.daily.map((d) => d.day.slice(5, 10)),
    datasets: [
      { label: 'Hiển thị', data: info.daily.map((d) => d.impressions), backgroundColor: '#0071A6', borderRadius: 4 },
    ],
  };
  const chartOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } };

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-dark2 shadow-lg dark:shadow-none p-4 rounded-md space-y-3">
        <div className="flex items-center justify-between gap-3">
          <span className="font-bold text-lg">Kiếm tiền từ quảng cáo</span>
          <button
            type="button"
            role="switch"
            aria-checked={info.adsEnabled}
            aria-label="Bật kiếm tiền quảng cáo"
            disabled={saving}
            onClick={toggle}
            className={`relative h-6 w-11 rounded-full transition ${info.adsEnabled ? 'bg-[#0071A6]' : 'bg-slate-300'} disabled:opacity-50`}
          >
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${info.adsEnabled ? 'left-[22px]' : 'left-0.5'}`}
            />
          </button>
        </div>
        <p className="text-sm ui-muted">
          Bạn nhận {info.sharePercent}% doanh thu mỗi lượt quảng cáo hợp lệ trên nội dung của bạn. Tiền được giữ{' '}
          {info.holdDays} ngày rồi cộng vào ví, rút như tiền donate.
        </p>
        <div className="space-y-1.5">
          {([
            ['Auto', 'Tự động (Cab chọn quảng cáo phù hợp)'],
            ['Manual', 'Tự chọn doanh nghiệp (chỉ hiện quảng cáo bạn đã tick)'],
          ] as const).map(([m, label]) => (
            <label key={m} className="flex items-center gap-2 text-sm ui-text cursor-pointer">
              <input
                type="radio"
                name="creator-ads-mode"
                checked={info.mode === m}
                disabled={saving}
                onChange={() => changeMode(m)}
              />
              {label}
            </label>
          ))}
        </div>
        <ul className="space-y-1.5">
          {checks.map((c) => (
            <li key={c.label} className="flex items-center gap-2 text-sm ui-text">
              {c.ok ? (
                <CheckCircle2 size={16} className="text-green-600" />
              ) : (
                <XCircle size={16} className="text-red-500" />
              )}
              <span>{c.label}</span>
              {!c.ok && (
                <Link to={c.to} className="text-[#0071A6] underline text-xs">
                  {c.action}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>

      <Marketplace manual={info.mode === 'Manual'} />

      <div className="grid grid-cols-2 gap-3">
        <Kpi label="Đã nhận (Ca)" value={num(info.totals.creditedCa)} accent />
        <Kpi label="Đang giữ (ước tính)" value={num(info.totals.pendingCa)} />
        <Kpi label="Hiển thị 30 ngày" value={num(info.totals.impressions30d)} />
        <Kpi label="Click 30 ngày" value={num(info.totals.clicks30d)} />
      </div>

      <div className="rounded-2xl ui-card border ui-divider p-5">
        <div className="text-sm ui-muted mb-2">Hiển thị 30 ngày gần nhất</div>
        <div className="h-56">
          <Bar data={chartData} options={chartOpts} />
        </div>
        <p className="text-xs ui-muted mt-2">
          Số ngày chưa cộng ví là ước tính; số tiền thực nhận tính theo tiền nhà quảng cáo đã trả.
        </p>
      </div>
    </div>
  );
};

export default CreatorAdsSetting;
